import { QueryResult } from '@apollo/client';
import React, { useContext, useMemo } from 'react';
import EDCollapse from '../../../../../../../../../BaseComponents/Collapsables/Collapse/EDCollapse';
import EDDivider from '../../../../../../../../../BaseComponents/Divider/EDDivider';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import EDList from '../../../../../../../../../BaseComponents/List/EDList';
import EDAvenyProgress from '../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDSwitch from '../../../../../../../../../BaseComponents/Switch/EDSwitch';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import { TEDLRFakturaAttestAttestTypEnum } from '../../../../../../../../../globalQueryTypes';
import { findFormatter } from '../../../../../../../../../Utils/Formatters/FindFormatter';
import FakturaDelContext from '../../../FakturaDelContextManager/FakturaDelContext';
import {
  GetKonteringHistory,
  GetKonteringHistoryVariables,
  GetKonteringHistory_node_TEDLRLeverantor,
  GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node,
  GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All,
} from '../../Queries/_QueryTypes/GetKonteringHistory';
import { KonteringRecept_db_LRKonteringsrecept_All } from '../../Queries/_QueryTypes/KonteringRecept';
import { createBills } from '../../Utils/KonteringHistoryModalUtils';
import FetchMoreButton from '../FetchMoreButton/FetchMoreButton';
import KonteringHistoryRow from '../KonteringHistoryRow/KonteringHistoryRow';

interface IProps {
  fetchingMore: boolean;
  konteringQuery: QueryResult<
    GetKonteringHistory,
    GetKonteringHistoryVariables
  >;
  othersKonteringQuery: QueryResult<
    GetKonteringHistory,
    GetKonteringHistoryVariables
  >;
  selectedKonteringar: GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All[];
  selectedRecept: KonteringRecept_db_LRKonteringsrecept_All[];
  setAdditionalInfo: React.Dispatch<
    React.SetStateAction<GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All | null>
  >;
  setAdditionalInfoBillDate: React.Dispatch<React.SetStateAction<string>>;
  setAdditionalInfoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchingMore: React.Dispatch<React.SetStateAction<boolean>>;

  setSelectedKonteringar: React.Dispatch<
    React.SetStateAction<
      GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All[]
    >
  >;
  setShowOthers: React.Dispatch<React.SetStateAction<boolean>>;
  showOthers: boolean;
  supplierID: string;
}

const KonteringHistory: React.FC<React.PropsWithChildren<IProps>> = props => {
  const fakturaDelContext = useContext(FakturaDelContext);

  const queryData = props.konteringQuery.data
    ?.node as GetKonteringHistory_node_TEDLRLeverantor;
  const othersQueryData = props.othersKonteringQuery.data
    ?.node as GetKonteringHistory_node_TEDLRLeverantor;

  const bills: GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node[] =
    useMemo(
      () =>
        queryData?._Associations?.Fakturor_Connection?.edges
          ?.map(createBills)
          .filter(
            bill => bill?._Associations?.Konteringsforslag_All?.length !== 0
          ) || [],
      [queryData?._Associations?.Fakturor_Connection?.edges]
    );

  const myBills = useMemo(
    () =>
      bills.filter(
        bill =>
          bill?._Associations?.Konteringsforslag_All?.length !== 0 &&
          bill?.AttesteradAvSign?.includes(
            fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
              ?.AttestantSign ?? ''
          ) &&
          bill?._Associations?.Konteringsforslag_All?.filter(
            kontering =>
              kontering._Associations?.Fakturaattest?.AttestantSign ===
                fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
                  ?.AttestantSign ||
              fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
                ?.AttestTyp ===
                TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
          ).length !== 0
      ),
    [bills, fakturaDelContext.minFakturaAttest.aktuellFakturaAttest]
  );

  const othersUsersBills = useMemo(
    () =>
      othersQueryData?._Associations?.Fakturor_Connection?.edges
        ?.map(createBills)
        .filter(bill => {
          const konteringar =
            bill?._Associations?.Konteringsforslag_All?.filter(
              (kontering, index) => {
                return (
                  kontering._Associations?.Fakturaattest?.AttestantSign !==
                  fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
                    ?.AttestantSign
                );
              }
            ) || [];

          return (
            bill?._Associations?.Konteringsforslag_All?.length !== 0 &&
            konteringar.length >= 1
          );
        }) || [],
    [
      othersQueryData?._Associations?.Fakturor_Connection?.edges,
      fakturaDelContext.minFakturaAttest.aktuellFakturaAttest?.AttestantSign,
    ]
  );

  return (
    <EDBox
      sx={theme => ({
        marginY: 0,
        mx: 0.25,
        py: 0.2,
        px: 2,
        overflowY: 'auto',
        height: '100%',
        marginBottom: 0,
        [theme.breakpoints.up('md')]: {
          height: theme.spacing(75),
        },
      })}
    >
      {props.konteringQuery.data ? (
        <>
          <EDTypography variant="h6" bold>
            Historik för {`${queryData?.LEVERANTORSNR} - ${queryData?.Namn}`}
          </EDTypography>

          <EDBox
            component="span"
            sx={{
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <EDSwitch
              value={props.showOthers}
              checked={props.showOthers}
              onChange={(event: any) => {
                props.setShowOthers(event.target.checked);
              }}
              title="Inkludera även andras historik"
            />
            <EDTypography bold color="textSecondary">
              Inkludera även andras historik
            </EDTypography>
          </EDBox>

          <EDCollapse open={props.showOthers}>
            <>
              <EDTypography variant="h6" bold>
                Andras historik för denna leverantör
              </EDTypography>
              {!props.othersKonteringQuery.data ? (
                <EDBox
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%',
                    height: '64px',
                  }}
                >
                  <EDAvenyProgress />
                </EDBox>
              ) : (
                <>
                  <EDList>
                    {othersUsersBills && othersUsersBills.length > 0 ? (
                      othersUsersBills.map((bill, otherUsersBillsIndex) => {
                        const konteringar =
                          bill?._Associations?.Konteringsforslag_All?.filter(
                            kontering =>
                              kontering._Associations?.Fakturaattest
                                ?.AttestantSign !==
                              fakturaDelContext.minFakturaAttest
                                .aktuellFakturaAttest?.AttestantSign
                          ) || [];
                        return (
                          <EDBox
                            key={otherUsersBillsIndex}
                            sx={{
                              my: 0.75,
                              mx: 0,
                              '&:first-of-type': {
                                marginTop: 0,
                              },
                            }}
                          >
                            <EDTypography variant="subtitle2" bold>
                              {`Faktura ${bill?.FAKTURA_NR} | ${
                                bill?.FAKTURADATUM
                              } | ${findFormatter(
                                'TEDFormatCurr'
                              ).displayer.display(
                                bill?.Fakturabelopp2,
                                true
                              )} kr`}
                            </EDTypography>

                            {konteringar.map((kontering, konteringIndex) => {
                              const disableKontering =
                                (props.selectedKonteringar.length !== 0 &&
                                  !props.selectedKonteringar.includes(
                                    kontering
                                  )) ||
                                props.selectedRecept.length !== 0;
                              return (
                                <KonteringHistoryRow
                                  key={konteringIndex}
                                  bill={bill}
                                  disableKontering={disableKontering}
                                  index={konteringIndex}
                                  kontering={kontering}
                                  selectedKonteringar={
                                    props.selectedKonteringar
                                  }
                                  selectedRecept={props.selectedRecept}
                                  setAdditionalInfo={props.setAdditionalInfo}
                                  setAdditionalInfoBillDate={
                                    props.setAdditionalInfoBillDate
                                  }
                                  setAdditionalInfoOpen={
                                    props.setAdditionalInfoOpen
                                  }
                                  setSelectedKonteringar={
                                    props.setSelectedKonteringar
                                  }
                                  testId={`konteringHistoryDialog-otherUsersBills-${otherUsersBillsIndex}-row-${konteringIndex}`}
                                />
                              );
                            })}
                          </EDBox>
                        );
                      })
                    ) : (
                      <EDTypography>
                        Ingen historik från andra användare
                      </EDTypography>
                    )}
                  </EDList>
                  <EDBox
                    component="span"
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <FetchMoreButton
                      fetchingMore={props.fetchingMore}
                      setFetchingMore={props.setFetchingMore}
                      supplierID={props.supplierID}
                      query={props.othersKonteringQuery}
                      queryData={othersQueryData}
                    />
                  </EDBox>
                </>
              )}
            </>
          </EDCollapse>

          {props.showOthers && (
            <>
              <EDDivider marginY={1} />
              <EDTypography variant="h6" bold>
                Min historik för denna leverantör
              </EDTypography>
            </>
          )}

          <EDList>
            {myBills && myBills.length > 0 ? (
              myBills.map((bill, myBillIndex) => {
                return (
                  <EDBox
                    key={myBillIndex}
                    sx={{
                      my: 0.75,
                      mx: 0,
                      '&:first-of-type': {
                        marginTop: 0,
                      },
                    }}
                  >
                    <EDTypography variant="subtitle2" bold>
                      {`Faktura ${bill?.FAKTURA_NR} | ${
                        bill?.FAKTURADATUM
                      } | ${findFormatter('TEDFormatCurr').displayer.display(
                        bill?.Fakturabelopp2,
                        true
                      )} kr`}
                    </EDTypography>

                    {bill?._Associations?.Konteringsforslag_All?.filter(
                      kontering =>
                        kontering._Associations?.Fakturaattest
                          ?.AttestantSign ===
                          fakturaDelContext.minFakturaAttest
                            .aktuellFakturaAttest?.AttestantSign ||
                        fakturaDelContext.minFakturaAttest.aktuellFakturaAttest
                          ?.AttestTyp ===
                          TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST
                    ).map((kontering, myBillKonteringforslagIndex) => {
                      const disableKontering =
                        (props.selectedKonteringar.length !== 0 &&
                          !props.selectedKonteringar.includes(kontering)) ||
                        props.selectedRecept.length !== 0;
                      return (
                        <KonteringHistoryRow
                          key={myBillKonteringforslagIndex}
                          bill={bill}
                          disableKontering={disableKontering}
                          index={myBillKonteringforslagIndex}
                          kontering={kontering}
                          selectedKonteringar={props.selectedKonteringar}
                          selectedRecept={props.selectedRecept}
                          setAdditionalInfo={props.setAdditionalInfo}
                          setAdditionalInfoBillDate={
                            props.setAdditionalInfoBillDate
                          }
                          setAdditionalInfoOpen={props.setAdditionalInfoOpen}
                          setSelectedKonteringar={props.setSelectedKonteringar}
                          testId={`konteringHistoryDialog-bill-${myBillIndex}-row-${myBillKonteringforslagIndex}`}
                        />
                      );
                    })}
                    {myBillIndex !== myBills.length - 1 && (
                      <EDDivider marginY={1} />
                    )}
                  </EDBox>
                );
              })
            ) : (
              <EDTypography>
                Du har ingen historik för denna leverantör
              </EDTypography>
            )}
          </EDList>
          <EDBox
            component="span"
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <FetchMoreButton
              fetchingMore={props.fetchingMore}
              setFetchingMore={props.setFetchingMore}
              supplierID={props.supplierID}
              query={props.konteringQuery}
              queryData={queryData}
            />
          </EDBox>
        </>
      ) : (
        <EDAvenyProgress />
      )}
    </EDBox>
  );
};

export default KonteringHistory;
